<template>
    <div class="products-wrapper">
        <div class="products-header">
            <div>
                <h1>{{ $tl("labels.modules.orderingTable.products.title") }}</h1>
                <p class="subtitle">{{ $tl("labels.modules.orderingTable.products.subtitle") }}</p>
            </div>
            <div class="products-meta" v-if="productsCount > 0">
                <span class="pill pill-soft">
                    {{ $tc("labels.modules.orderingTable.products.count", productsCount, { count: productsCount }) }}
                </span>
            </div>
        </div>

        <div class="products-toolbar">
            <form class="search-form" @submit.prevent="applySearch">
                <div class="search-field">
                    <input
                        v-model.trim="searchTerm"
                        type="search"
                        class="search-input"
                        :placeholder="$tl('labels.modules.orderingTable.products.search.placeholder')" />
                    <button type="submit" class="btn btn-sm btn-outline-secondary btn-circle" :disabled="loading">
                        {{ $tl("labels.modules.orderingTable.products.search.action") }}
                    </button>
                </div>
                <div v-if="isReordering" class="state state-loading toolbar-reorder-status">
                    <span class="dot"></span>
                    {{ $tl("labels.modules.orderingTable.products.reorder.saving") }}
                </div>
                <div v-else-if="reorderError" class="state state-error toolbar-reorder-status">
                    {{ reorderError }}
                </div>
            </form>
            <select
                id="products-category-filter"
                v-model="selectedCategoryId"
                class="form-select align-middle d-inline-block category-filter-select"
                :aria-label="$tl('labels.modules.orderingTable.products.table.category')"
                :disabled="categoriesLoading"
                @change="applyCategoryFilter">
                <option value="all">{{ $tl("labels.modules.orderingTable.products.filters.allCategories") }}</option>
                <option v-for="category in categories" :key="category.id" :value="String(category.id)">
                    {{ category.name }}
                </option>
            </select>
            <button v-if="searchActive" type="button" class="ghost-button" @click="clearSearch">
                {{ $tl("labels.modules.orderingTable.products.search.clear") }}
            </button>
        </div>

        <div class="categories-panel">
            <div class="categories-head">
                <div>
                    <h2>{{ $tl("labels.modules.orderingTable.products.categories.title") }}</h2>
                    <p class="categories-subtitle">
                        {{ $tl("labels.modules.orderingTable.products.categories.subtitle") }}
                    </p>
                </div>
                <span class="pill pill-soft">
                    {{ $tc("labels.modules.orderingTable.products.categories.count", categories.length, { count: categories.length }) }}
                </span>
            </div>

            <div v-if="categoriesLoading" class="state state-loading state-inline">
                <span class="dot"></span>
                {{ $tl("labels.modules.orderingTable.products.categories.loading") }}
            </div>
            <div v-else-if="categoriesError" class="state state-error state-inline">
                {{ categoriesError }}
            </div>
            <div v-else-if="!categories.length" class="state state-empty state-inline">
                {{ $tl("labels.modules.orderingTable.products.categories.empty") }}
            </div>
            <div v-else>
                <div v-if="isReorderingCategories" class="state state-loading state-inline">
                    <span class="dot"></span>
                    {{ $tl("labels.modules.orderingTable.products.categories.reorder.saving") }}
                </div>
                <draggable
                    class="categories-list"
                    tag="div"
                    v-model="categories"
                    handle=".category-drag-handle"
                    :disabled="!canDragCategories"
                    ghost-class="drag-ghost"
                    chosen-class="drag-chosen"
                    drag-class="drag-dragging"
                    @end="onCategoriesReordered">
                    <div v-for="category in categories" :key="category.id" class="category-card">
                        <span class="category-drag-handle" :class="{ 'drag-handle-disabled': !canDragCategories }">
                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                stroke="currentColor"
                                stroke-width="2"
                                fill="none"
                                stroke-linecap="round"
                                stroke-linejoin="round">
                                <line x1="3" y1="9" x2="21" y2="9"></line>
                                <line x1="3" y1="15" x2="21" y2="15"></line>
                            </svg>
                        </span>
                        <div class="category-content">
                            <div class="category-name">{{ category.name }}</div>
                            <div class="category-meta">
                                {{
                                    $tc("labels.modules.orderingTable.products.categories.productsCount", category.product_count, {
                                        count: category.product_count,
                                    })
                                }}
                            </div>
                        </div>
                    </div>
                </draggable>
            </div>
        </div>

        <div v-if="loading" class="state state-loading">
            <span class="dot"></span>
            {{ $tl("labels.modules.orderingTable.products.loading") }}
        </div>

        <div v-else-if="error" class="state state-error">
            {{ error }}
        </div>

        <div v-else-if="!products || !products.length" class="state state-empty">
            {{ $tl("labels.modules.orderingTable.products.empty") }}
        </div>

        <div v-else class="table-container">
            <div
                v-if="showPrevDropZone"
                class="drop-zone drop-zone-prev"
                :class="{ 'drop-zone-active': dragOverZone === 'prev' }"
                @dragenter.prevent="dragOverZone = 'prev'"
                @dragover.prevent
                @dragleave.prevent="clearDragOver('prev')"
                @drop.prevent="dropOnPrevPage">
                {{ $tl("labels.modules.orderingTable.products.reorder.movePrev") }}
            </div>
            <table class="products-table">
                <thead>
                    <tr>
                        <th class="col-drag">{{ $tl("labels.modules.orderingTable.products.table.order") }}</th>
                        <th>{{ $tl("labels.modules.orderingTable.products.table.name") }}</th>
                        <th>{{ $tl("labels.modules.orderingTable.products.table.category") }}</th>
                        <th class="col-price">{{ $tl("labels.modules.orderingTable.products.table.price") }}</th>
                        <th class="col-status">{{ $tl("labels.modules.orderingTable.products.table.status") }}</th>
                        <th class="col-actions">{{ $tl("labels.modules.orderingTable.products.table.actions") }}</th>
                    </tr>
                </thead>
                <draggable
                    tag="tbody"
                    v-model="products"
                    handle=".drag-handle"
                    :disabled="!canDrag"
                    ghost-class="drag-ghost"
                    chosen-class="drag-chosen"
                    drag-class="drag-dragging"
                    @start="onDragStart"
                    @end="onDragEnd">
                    <tr v-for="product in products" :key="product.id">
                        <td class="col-drag">
                            <span class="drag-handle" :class="{ 'drag-handle-disabled': !canDrag }">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="3" y1="9" x2="21" y2="9"></line>
                                    <line x1="3" y1="15" x2="21" y2="15"></line>
                                </svg>
                            </span>
                        </td>
                        <td>
                            <div class="product-cell">
                                <div class="product-thumb">
                                    <img :src="productImage(product)" @error="onProductImageError" alt="" />
                                </div>
                                <div class="product-text">
                                    <div class="product-name">{{ product.name }}</div>
                                    <div v-if="product.description" class="product-description">
                                        {{ product.description }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td>{{ categoryLabel(product) }}</td>
                        <td class="col-price">{{ priceLabel(product) }}</td>
                        <td class="col-status">
                            <span :class="['status-pill', availabilityClass(product)]">
                                {{ availabilityLabel(product) }}
                            </span>
                        </td>
                        <td class="col-actions">
                            <button type="button" class="ghost-button" @click="openDetails(product)">
                                {{ $tl("labels.detail") }}
                            </button>
                        </td>
                    </tr>
                </draggable>
            </table>
            <div
                v-if="showNextDropZone"
                class="drop-zone drop-zone-next"
                :class="{ 'drop-zone-active': dragOverZone === 'next' }"
                @dragenter.prevent="dragOverZone = 'next'"
                @dragover.prevent
                @dragleave.prevent="clearDragOver('next')"
                @drop.prevent="dropOnNextPage">
                {{ $tl("labels.modules.orderingTable.products.reorder.moveNext") }}
            </div>
        </div>

        <Pagination
            v-if="showPagination"
            class="mt-2"
            :nb-pages="paginationPages"
            :nb-results="productsCount"
            :nb-results-per-page="pagination.per_page"
            :current-page="pagination.current_page"
            :page-size-options="perPageOptions"
            :include-all="false"
            @set-current-page="goToPage"
            @set-nb-results-per-page="updatePerPage" />

        <modal v-if="detail.visible" @close="closeDetailFromModal" size="lg">
            <h5 slot="header">
                {{ $tl("labels.modules.orderingTable.products.detail.title") }}
            </h5>
            <div slot="body">
                <div v-if="detail.loading" class="state state-loading">
                    <span class="dot"></span>
                    {{ $tl("labels.modules.orderingTable.products.detail.loading") }}
                </div>
                <div v-else-if="detail.error" class="state state-error">
                    {{ detail.error }}
                </div>
                <div v-else-if="!detail.data" class="state state-empty">
                    {{ $tl("labels.modules.orderingTable.products.detail.empty") }}
                </div>
                <div v-else>
                    <div class="detail-grid">
                        <div class="detail-media">
                            <img :src="productImage(detail.data)" @error="onProductImageError" alt="" />
                        </div>
                        <div class="detail-info">
                            <div class="detail-title">{{ detail.data.name }}</div>
                            <div v-if="detail.data.description" class="detail-description">
                                {{ detail.data.description }}
                            </div>
                            <div class="detail-meta">
                                <div class="detail-meta-row">
                                    <span class="detail-label">{{ $tl("labels.modules.orderingTable.products.detail.categories") }}</span>
                                    <span>{{ detailCategoryLabel(detail.data) }}</span>
                                </div>
                                <div class="detail-meta-row">
                                    <span class="detail-label">{{ $tl("labels.modules.orderingTable.products.detail.availability") }}</span>
                                    <span :class="['status-pill', availabilityClass(detail.data)]">
                                        {{ availabilityLabel(detail.data) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="detail-section">
                        <h6>{{ $tl("labels.modules.orderingTable.products.detail.variants") }}</h6>
                        <div v-if="!detailVariants.length" class="detail-empty">
                            {{ $tl("labels.modules.orderingTable.products.detail.noVariants") }}
                        </div>
                        <div v-else class="variants-list">
                            <div v-for="variant in detailVariants" :key="variant.id" class="variant-card">
                                <div class="variant-header">
                                    <div class="variant-title">
                                        <span class="variant-name">{{ detailDisplayLabel(variant.name) }}</span>
                                        <span v-if="variant.is_default" class="variant-default">
                                            {{ $tl("labels.modules.orderingTable.products.detail.defaultVariant") }}
                                        </span>
                                    </div>
                                    <div class="variant-meta">
                                        <span class="variant-price">{{ formatMoney(variant.price_cents, variant.currency) }}</span>
                                        <span :class="['status-pill', variantAvailabilityClass(variant)]">
                                            {{ variantAvailabilityLabel(variant) }}
                                        </span>
                                    </div>
                                </div>

                                <div v-if="variant.option_groups && variant.option_groups.length" class="variant-options">
                                    <div v-for="group in variant.option_groups" :key="group.id" class="option-group">
                                        <div class="option-group-title">{{ group.name }}</div>
                                        <ul v-if="group.items && group.items.length" class="option-items">
                                            <li v-for="item in group.items" :key="item.id">
                                                {{ optionItemLabel(item) }}
                                            </li>
                                        </ul>
                                        <div v-else class="option-empty">
                                            {{ $tl("labels.modules.orderingTable.products.detail.noOptions") }}
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="option-empty">
                                    {{ $tl("labels.modules.orderingTable.products.detail.noOptions") }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-outline-secondary btn-circle btn-sm" @click="closeDetail">
                    {{ $tl("labels.modules.orderingTable.products.detail.close") }}
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import Pagination from "../../../components/Datatable/Pagination.vue";

const DEFAULT_PRODUCT_IMAGE =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="10" fill="%23F3F4F6"/><circle cx="24" cy="24" r="7" fill="%23D1D5DB"/><path d="M12 46l11-11a4 4 0 0 1 5.66 0L33.3 39.6a4 4 0 0 0 5.66 0l3.08-3.08a4 4 0 0 1 5.66 0L52 40.83V52H12V46Z" fill="%239CA3AF"/></svg>';

export default {
    data() {
        return {
            loading: true,
            error: null,
            products: [],
            categories: [],
            categoriesLoading: false,
            categoriesError: null,
            isReorderingCategories: false,
            pagination: {
                current_page: 1,
                per_page: 10,
                total: 0,
                total_pages: 1,
            },
            perPageOptions: [10, 20, 50],
            searchTerm: "",
            search: "",
            selectedCategoryId: "all",
            isReordering: false,
            reorderError: null,
            isDragging: false,
            dragOverZone: null,
            draggedProduct: null,
            skipReorder: false,
            detail: {
                visible: false,
                loading: false,
                error: null,
                data: null,
            },
        };
    },
    computed: {
        restaurantId() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        productsCount() {
            if (Number.isFinite(this.pagination.total) && this.pagination.total > 0) {
                return this.pagination.total;
            }
            return this.products.length;
        },
        showPagination() {
            return this.pagination.total_pages > 1 || this.products.length > 0;
        },
        paginationPages() {
            return Math.max(1, this.pagination.total_pages || 1);
        },
        canPrev() {
            return this.pagination.current_page > 1;
        },
        canNext() {
            return this.pagination.current_page < this.pagination.total_pages;
        },
        canDrag() {
            return !this.loading && !this.isReordering && this.products.length > 1;
        },
        canDragCategories() {
            return !this.categoriesLoading && !this.isReorderingCategories && this.categories.length > 1;
        },
        searchActive() {
            return this.search !== "";
        },
        showPrevDropZone() {
            return this.isDragging && this.canPrev;
        },
        showNextDropZone() {
            return this.isDragging && this.canNext;
        },
        detailName() {
            return this.detail.data && this.detail.data.name ? this.detail.data.name : "";
        },
        detailVariants() {
            return this.detail.data && Array.isArray(this.detail.data.variants) ? this.detail.data.variants : [];
        },
    },
    methods: {
        hasValidRestaurantId() {
            return Number.isFinite(this.restaurantId) && this.restaurantId > 0;
        },
        isSuccessfulApiResponse(response) {
            return Boolean(response && response.status >= 200 && response.status < 300 && response.data && typeof response.data === "object");
        },
        async requestCatalogGet(path) {
            const response = await this.httpGet(`/api${path}`, {
                handleReject: false,
                config: {
                    validateStatus: () => true,
                },
            });

            return this.isSuccessfulApiResponse(response) ? response : false;
        },
        async requestCatalogPost(path, payload) {
            try {
                const response = await this.httpPost(`/api${path}`, payload, {
                    handleResolve: false,
                    handleReject: false,
                    config: {
                        validateStatus: () => true,
                    },
                });

                return this.isSuccessfulApiResponse(response) ? response : false;
            } catch (error) {
                return false;
            }
        },
        fetchCategories() {
            if (!this.hasValidRestaurantId()) {
                this.categoriesLoading = false;
                this.categoriesError = this.$tl("labels.modules.orderingTable.products.categories.error");
                return;
            }

            this.categoriesLoading = true;
            this.categoriesError = null;

            this.requestCatalogGet(`/ordering-table/restaurants/${this.restaurantId}/table-service/categories`)
                .then((response) => {
                    if (!response || !response.data || !Array.isArray(response.data.data)) {
                        this.categoriesError = this.$tl("labels.modules.orderingTable.products.categories.error");
                        return;
                    }

                    this.categories = response.data.data;
                })
                .finally(() => {
                    this.categoriesLoading = false;
                });
        },
        fetchProducts(page = 1, { showLoading = true } = {}) {
            if (!this.hasValidRestaurantId()) {
                this.loading = false;
                this.error = this.$tl("labels.modules.orderingTable.products.error");
                return;
            }

            if (showLoading) {
                this.loading = true;
            }
            this.error = null;

            const perPage = this.pagination.per_page || 20;
            const search = this.search ? this.search.trim() : "";
            const categoryId = Number.parseInt(this.selectedCategoryId, 10);
            let path = `/ordering-table/restaurants/${this.restaurantId}/table-service/products?page=${page}&per_page=${perPage}`;

            if (search !== "") {
                path += `&search=${encodeURIComponent(search)}`;
            }

            if (Number.isFinite(categoryId) && categoryId > 0) {
                path += `&category_id=${categoryId}`;
            }

            this.requestCatalogGet(path)
                .then((response) => {
                    if (!response || !response.data) {
                        this.error = this.$tl("labels.modules.orderingTable.products.error");
                        return;
                    }

                    this.products = Array.isArray(response.data.data) ? response.data.data : [];
                    const pagination = response.data.meta && response.data.meta.pagination ? response.data.meta.pagination : null;
                    if (pagination) {
                        this.pagination = {
                            current_page: pagination.current_page || 1,
                            per_page: pagination.per_page || perPage,
                            total: pagination.total || 0,
                            total_pages: pagination.total_pages || 1,
                        };
                    }
                })
                .finally(() => {
                    if (showLoading) {
                        this.loading = false;
                    }
                });
        },
        onCategoriesReordered(event) {
            if (!event || event.oldIndex === event.newIndex) {
                return;
            }

            this.persistCategoriesOrder();
        },
        persistCategoriesOrder() {
            if (!this.categories.length) {
                return;
            }

            this.isReorderingCategories = true;
            this.categoriesError = null;

            this.requestCatalogPost(`/ordering-table/restaurants/${this.restaurantId}/table-service/categories/reorder`, {
                category_ids: this.categories.map((category) => category.id),
            })
                .then((response) => {
                    if (!response) {
                        this.categoriesError = this.$tl("labels.modules.orderingTable.products.categories.reorder.error");
                    }
                })
                .finally(() => {
                    this.isReorderingCategories = false;
                    this.fetchCategories();
                    this.fetchProducts(this.pagination.current_page, { showLoading: false });
                });
        },
        goToPage(page) {
            if (page < 1 || page > this.pagination.total_pages || page === this.pagination.current_page) {
                return;
            }
            this.pagination.current_page = page;
            this.fetchProducts(page);
        },
        updatePerPage(value) {
            const perPageValue = value === "all" ? Math.min(100, this.pagination.total || 100) : Number.parseInt(value, 10);

            if (!Number.isFinite(perPageValue) || perPageValue < 1) {
                return;
            }

            if (perPageValue === this.pagination.per_page) {
                return;
            }

            this.pagination.per_page = perPageValue;
            this.pagination.current_page = 1;
            this.fetchProducts(1);
        },
        applySearch() {
            const nextSearch = this.searchTerm.trim();

            if (nextSearch === this.search && this.pagination.current_page === 1) {
                return;
            }

            this.search = nextSearch;
            this.pagination.current_page = 1;
            this.fetchProducts(1);
        },
        clearSearch() {
            if (!this.search && !this.searchTerm) {
                return;
            }

            this.searchTerm = "";
            this.search = "";
            this.pagination.current_page = 1;
            this.fetchProducts(1);
        },
        applyCategoryFilter() {
            this.pagination.current_page = 1;
            this.fetchProducts(1);
        },
        categoryLabel(product) {
            if (!product || !Array.isArray(product.categories) || product.categories.length === 0) {
                return this.$tl("labels.modules.orderingTable.products.uncategorized");
            }
            return product.categories
                .map((category) => category.name)
                .filter(Boolean)
                .join(", ");
        },
        detailCategoryLabel(product) {
            if (!product || !Array.isArray(product.categories) || product.categories.length === 0) {
                return this.$tl("labels.modules.orderingTable.products.uncategorized");
            }
            return product.categories
                .map((category) => category.name)
                .filter(Boolean)
                .join(", ");
        },
        priceLabel(product) {
            if (product && product.price_cents != null) {
                return this.formatMoney(product.price_cents, product.currency);
            }
            return "--";
        },
        productImage(product) {
            return product && product.image ? product.image : DEFAULT_PRODUCT_IMAGE;
        },
        onProductImageError(event) {
            if (!event || !event.target || event.target.dataset.fallbackApplied === "1") return;
            event.target.dataset.fallbackApplied = "1";
            event.target.src = DEFAULT_PRODUCT_IMAGE;
        },
        formatMoney(cents, currency) {
            if (cents == null) return "--";
            return (cents / 100).toLocaleString("fr-FR", {
                style: "currency",
                currency: currency || "EUR",
            });
        },
        isAvailable(product) {
            if (!product || product.is_available === false) {
                return false;
            }
            if (product.variant_is_available === false) {
                return false;
            }
            if (product.variant_is_available == null && Array.isArray(product.variants) && product.default_variant_id != null) {
                const defaultVariant = product.variants.find((variant) => variant.id === product.default_variant_id);
                if (defaultVariant && defaultVariant.is_available === false) {
                    return false;
                }
            }
            return true;
        },
        availabilityLabel(product) {
            return this.isAvailable(product)
                ? this.$tl("labels.modules.orderingTable.products.status.active")
                : this.$tl("labels.modules.orderingTable.products.status.inactive");
        },
        availabilityClass(product) {
            return this.isAvailable(product) ? "status-active" : "status-inactive";
        },
        variantAvailabilityLabel(variant) {
            return variant && variant.is_available
                ? this.$tl("labels.modules.orderingTable.products.status.active")
                : this.$tl("labels.modules.orderingTable.products.status.inactive");
        },
        variantAvailabilityClass(variant) {
            return variant && variant.is_available ? "status-active" : "status-inactive";
        },
        optionItemLabel(item) {
            const label =
                item && item.name ? this.detailDisplayLabel(item.name) : this.$tl("labels.modules.orderingTable.products.detail.optionFallback");
            if (!item || item.price_delta_cents == null || item.price_delta_cents === 0) {
                return label;
            }
            const sign = item.price_delta_cents > 0 ? "+" : "-";
            const price = this.formatMoney(Math.abs(item.price_delta_cents), item.currency);
            return `${label} (${sign}${price})`;
        },
        detailDisplayLabel(value) {
            if (typeof value !== "string" || value.trim() === "") {
                return this.$tl("labels.modules.orderingTable.products.detail.standardVariantName");
            }

            return value.trim().toLowerCase() === "default" ? this.$tl("labels.modules.orderingTable.products.detail.standardVariantName") : value;
        },
        onDragStart(event) {
            this.isDragging = true;
            this.dragOverZone = null;
            this.skipReorder = false;
            this.draggedProduct = event && Number.isInteger(event.oldIndex) ? this.products[event.oldIndex] : null;
        },
        onDragEnd(event) {
            const shouldSkip = this.skipReorder;
            this.isDragging = false;
            this.dragOverZone = null;
            this.draggedProduct = null;
            this.skipReorder = false;

            if (shouldSkip || !event || event.oldIndex === event.newIndex) {
                return;
            }

            const newIndex = event.newIndex;
            const movedProduct = this.products[newIndex];
            if (!movedProduct) {
                return;
            }

            const beforeProduct = newIndex > 0 ? this.products[newIndex - 1] : null;
            const afterProduct = newIndex < this.products.length - 1 ? this.products[newIndex + 1] : null;

            this.reorderProduct({
                productId: movedProduct.id,
                beforeId: beforeProduct ? beforeProduct.id : null,
                afterId: afterProduct ? afterProduct.id : null,
            });
        },
        clearDragOver(zone) {
            if (this.dragOverZone === zone) {
                this.dragOverZone = null;
            }
        },
        dropOnPrevPage() {
            if (!this.draggedProduct || !this.canPrev) {
                return;
            }

            this.skipReorder = true;
            this.dragOverZone = null;

            this.reorderProduct({
                productId: this.draggedProduct.id,
                targetPage: this.pagination.current_page - 1,
                targetIndex: this.pagination.per_page,
            });
        },
        dropOnNextPage() {
            if (!this.draggedProduct || !this.canNext) {
                return;
            }

            this.skipReorder = true;
            this.dragOverZone = null;

            this.reorderProduct({
                productId: this.draggedProduct.id,
                targetPage: this.pagination.current_page + 1,
                targetIndex: 0,
            });
        },
        reorderProduct({ productId, beforeId = null, afterId = null, targetPage = null, targetIndex = null }) {
            if (!productId) {
                return;
            }

            this.isReordering = true;
            this.reorderError = null;

            const payload = {};

            if (beforeId != null) {
                payload.before_id = beforeId;
            }

            if (afterId != null) {
                payload.after_id = afterId;
            }

            if (beforeId == null && afterId == null && targetPage != null && targetIndex != null) {
                payload.target_page = targetPage;
                payload.target_index = targetIndex;
                payload.per_page = this.pagination.per_page || 20;
            }

            const refreshPage = targetPage || this.pagination.current_page;

            this.requestCatalogPost(`/ordering-table/restaurants/${this.restaurantId}/table-service/products/${productId}/reorder`, payload)
                .then((response) => {
                    if (!response) {
                        this.reorderError = this.$tl("labels.modules.orderingTable.products.reorder.error");
                    }
                })
                .finally(() => {
                    this.isReordering = false;
                    this.fetchProducts(refreshPage, { showLoading: false });
                });
        },
        openDetails(product) {
            this.detail.visible = true;
            this.detail.loading = true;
            this.detail.error = null;
            this.detail.data = null;

            this.requestCatalogGet(`/ordering-table/restaurants/${this.restaurantId}/table-service/products/${product.id}`)
                .then((response) => {
                    if (!response || !response.data || !response.data.data) {
                        this.detail.error = this.$tl("labels.modules.orderingTable.products.detail.error");
                        return;
                    }
                    this.detail.data = response.data.data;
                })
                .finally(() => (this.detail.loading = false));
        },
        closeDetail() {
            this.detail.visible = false;
        },
        closeDetailFromModal(e) {
            if (!e.target.classList.contains("modal-wrapper")) {
                e.stopPropagation();
            } else {
                this.closeDetail();
            }
        },
    },
    mounted() {
        if (this.hasValidRestaurantId()) {
            this.fetchCategories();
            this.fetchProducts(1);
            return;
        }

        this.loading = false;
        this.error = this.$tl("labels.modules.orderingTable.products.error");
    },
    components: {
        draggable,
        Pagination,
    },
};
</script>

<style scoped>
.products-wrapper {
    padding: 20px 24px;
    background: #f5f5f7;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
    font-family:
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
}

.products-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.products-header h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: #111827;
}

.subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #6b7280;
}

.products-meta {
    display: flex;
    align-items: center;
    gap: 8px;
}

.products-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.categories-panel {
    margin-bottom: 16px;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #ffffff;
}

.categories-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.categories-head h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
}

.categories-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #6b7280;
}

.categories-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
}

.category-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: linear-gradient(135deg, #ffffff, #f9fafb);
}

.category-drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    cursor: grab;
    flex: 0 0 auto;
}

.category-content {
    min-width: 0;
}

.category-name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
}

.category-meta {
    font-size: 12px;
    color: #6b7280;
}

.search-form {
    flex: 1 1 auto;
}

.search-field {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    max-width: 360px;
}

.category-filter-select {
    max-width: 260px;
    min-width: 220px;
}

.search-input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #111827;
    width: 100%;
}

.pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    border: 1px solid rgba(148, 163, 184, 0.4);
    color: #374151;
}

.pill-soft {
    background: #eef2ff;
}

.state {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    padding: 10px 12px;
    border-radius: 10px;
    background: #e5e7eb;
}

.state-loading {
    background: #e0f2fe;
}

.state-error {
    background: #fee2e2;
    color: #b91c1c;
}

.state-empty {
    background: #f3f4f6;
}

.state-inline {
    margin-bottom: 12px;
}

.state .dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #0284c7;
    animation: pulse 1s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(0.9);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.1);
        opacity: 1;
    }
    100% {
        transform: scale(0.9);
        opacity: 0.8;
    }
}

.table-container {
    margin-top: 12px;
    background: white;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
}

.products-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.products-table th,
.products-table td {
    padding: 12px 14px;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
}

.products-table th {
    text-align: left;
    font-size: 12px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: #f9fafb;
}

.col-drag {
    width: 60px;
}

.product-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.product-thumb {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    overflow: hidden;
    background: #f3f4f6;
    flex: 0 0 auto;
}

.product-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.product-name {
    font-weight: 600;
    color: #111827;
}

.product-description {
    margin-top: 2px;
    font-size: 12px;
    color: #6b7280;
}

.drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    color: #9ca3af;
    cursor: grab;
}

.drag-handle:hover {
    background: #f3f4f6;
    color: #4b5563;
}

.drag-handle-disabled {
    cursor: not-allowed;
    color: #d1d5db;
}

.drag-ghost {
    opacity: 0.5;
}

.drag-chosen {
    background: #f8fafc;
}

.drop-zone {
    padding: 10px 12px;
    text-align: center;
    font-size: 12px;
    color: #4b5563;
    border: 1px dashed #cbd5f5;
    background: #eef2ff;
}

.drop-zone-prev {
    border-bottom: none;
}

.drop-zone-next {
    border-top: none;
}

.drop-zone-active {
    background: #dbeafe;
    border-color: #60a5fa;
    color: #1d4ed8;
}

.col-price {
    font-variant-numeric: tabular-nums;
}

.col-status {
    width: 140px;
}

.col-actions {
    width: 140px;
}

.ghost-button {
    border-radius: 999px;
    border: 1px solid #e5e7eb;
    padding: 5px 12px;
    font-size: 12px;
    background: white;
    color: #111827;
    cursor: pointer;
    transition: all 0.15s ease;
}

.ghost-button:hover {
    border-color: #4f46e5;
    color: #4f46e5;
    box-shadow: 0 0 0 1px rgba(79, 70, 229, 0.1);
}

.status-pill {
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
}

.status-active {
    background: #dcfce7;
    color: #166534;
}

.status-inactive {
    background: #fee2e2;
    color: #b91c1c;
}

.detail-grid {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 16px;
    align-items: start;
}

.detail-media {
    width: 140px;
    height: 140px;
    border-radius: 16px;
    overflow: hidden;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 12px;
    text-align: center;
    padding: 12px;
}

.detail-media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.detail-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.detail-description {
    margin-top: 6px;
    font-size: 13px;
    color: #4b5563;
}

.detail-meta {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 13px;
}

.detail-meta-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.detail-label {
    font-weight: 600;
    color: #6b7280;
}

.detail-section {
    margin-top: 20px;
}

.detail-section h6 {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 10px;
}

.detail-empty {
    font-size: 12px;
    color: #6b7280;
}

.variants-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.variant-card {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 12px;
    background: #f9fafb;
}

.variant-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.variant-name {
    font-weight: 600;
    color: #111827;
}

.variant-default {
    margin-left: 8px;
    font-size: 10px;
    text-transform: uppercase;
    padding: 2px 6px;
    border-radius: 999px;
    background: #e0f2fe;
    color: #0369a1;
}

.variant-meta {
    display: flex;
    align-items: center;
    gap: 10px;
}

.variant-price {
    font-weight: 600;
    font-size: 13px;
}

.variant-options {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.option-group-title {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
}

.option-items {
    margin: 4px 0 0;
    padding-left: 16px;
    font-size: 12px;
    color: #4b5563;
}

.option-empty {
    margin-top: 4px;
    font-size: 12px;
    color: #6b7280;
}

@media (max-width: 768px) {
    .search-field {
        max-width: 100%;
    }

    .category-filter-select {
        width: 100%;
        max-width: 100%;
        min-width: 0;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }

    .detail-media {
        width: 100%;
        height: 200px;
    }
}
</style>
